import { Box, Stack, TextField } from "@mui/material";
import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";

import {
  AutoCompleteUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
} from "@4uhub/lib4uhub";

interface IMacroFilterProps {
  onFilterParams: Dispatch<SetStateAction<IFilterParams>>;
  filterParams: IFilterParams;
}

export interface IFilterParams {
  Identifier: string;
  InvoiceIdentifier: string;
  StatusItem: string;
  CreatedStart: Date | null;
  CreatedEnd?: Date | null;
  refresh?: boolean;
}

const translationPath = "page.4uBus.logs.nfeTotvs.";

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
}) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);

  const { t } = useTranslation();

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const [resetDateRange, setResetDateRange] = useState<boolean>(false);
  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setResetDateRange((prev) => !prev);
  };

  const [enableFields, setEnableFields] = useState<boolean>(true);

  const handleStartDate = (date: Date | null) => {
    setFilter({ CreatedStart: date });
  };
  const handleEndDate = (date: Date | null) => {
    setFilter({ CreatedEnd: date });
  };

  const [selectedStatus, setSelectedStatus] = useState<ISelectType | null>(
    null,
  );

  const handleSelectStatus = useCallback((value: ISelectType | null) => {
    setFilter({ StatusItem: value?.id ?? "" });
    setSelectedStatus(value);
  }, []);

  // const noFilterSelected =
  //   !filter.CreatedStart &&
  //   !filter.CreatedEnd &&
  //   !filter.StatusItem.trim() &&
  //   !filter.Identifier.trim() &&
  //   !filter.InvoiceIdentifier.trim();

  const statuses: ISelectType[] = useMemo(() => {
    return [
      {
        id: "PENDENTE",
        name: t(translationPath + "status.pending"),
        code: "P",
      },
      {
        id: "PROCESSANDO",
        name: t(translationPath + "status.processing"),
        code: "PR",
      },
      {
        id: "FINALIZADO",
        name: t(translationPath + "status.finished"),
        code: "F",
      },
      {
        id: "ERRO",
        name: t(translationPath + "status.error"),
        code: "E",
      },
    ];
  }, [t]);

  return (
    <Stack flexDirection={"row"} gap={2} alignItems="center">
      <DateRangeSelect
        setEndDate={handleEndDate}
        setStartDate={handleStartDate}
        setEnableFields={setEnableFields}
        resetToToday={resetDateRange}
      />
      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.CreatedStart}
          onChange={(e) => setFilter({ CreatedStart: e })}
          label={`${t(translationPath + "start_date")}`}
          isClearable={false}
          disable={enableFields}
        />
      </Box>
      <Box sx={{ flex: "0 0 160px" }}>
        <DatePickerUncontrolled
          value={filter.CreatedEnd}
          onChange={(e) => setFilter({ CreatedEnd: e })}
          label={`${t(translationPath + "end_date")}`}
          isClearable={false}
          disable={enableFields}
        />
      </Box>

      <TextField
        value={filter.Identifier}
        size="small"
        label={t(translationPath + "identifier")}
        fullWidth
        onChange={(e) => setFilter({ Identifier: e.target.value })}
      />

      <TextField
        value={filter.InvoiceIdentifier}
        size="small"
        label={t(translationPath + "invoice_identifier")}
        fullWidth
        onChange={(e) => setFilter({ InvoiceIdentifier: e.target.value })}
      />

      <AutoCompleteUncontrolled
        label={t(translationPath + "status_item") || "Extension"}
        onValueSelected={handleSelectStatus}
        value={selectedStatus}
        options={statuses}
        size="small"
        fullWidth
        sx={{ minWidth: 300 }}
      />

      <FiltersActions
        onFilter={filterHandler}
        onClean={cleanHandler}
        // disableFilterBtn={noFilterSelected}
        showCleanBtn={showCleanBtn}
      />
    </Stack>
  );
};

export default memo(MacroFilter);
